import React from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { Button } from '@pandiafi/design-system'
import { useNavigate } from 'react-router-dom'

import { ResidentAppError } from '../../../common/error'
import { ResidentAppErrorCode } from '../../../common/types'

import { Error } from 'components/Error'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

interface TerminateContractErrorHandlerProps {
  children: React.ReactNode
  view: 'form' | 'confirm'
}

export const TerminateContractErrorHandler: React.FC<
  TerminateContractErrorHandlerProps
> = ({ children, view }) => {
  const [t] = useTranslation(['terminateContract'])
  const navigate = useNavigate()
  const { terminateContractPath, confirmContractTerminationPath } =
    useContractPaths()

  const fallbackRender = ({ error, resetErrorBoundary }: FallbackProps) => {
    const restartPath =
      view === 'form' ? terminateContractPath : confirmContractTerminationPath

    const RestartButton = (
      <Button
        onClick={() => {
          resetErrorBoundary()
          navigate(restartPath, { replace: true })
        }}
      >
        {t('buttons.restart')}
      </Button>
    )

    const title =
      view === 'form' ? t('confirmationErrorText') : t('confirmErrorText')

    if (error instanceof ResidentAppError) {
      switch (error.errorCode) {
        case ResidentAppErrorCode.CONFIRM_OPERATION_GENERIC_ERROR:
          return <Error title={title} action={RestartButton} />

        case ResidentAppErrorCode.CONFIRM_OPERATION_TERMINATION_DATE_INVALID:
          return (
            <Error
              title={title}
              message={t('confirmationError.movingOutDateNoLongerValid')}
              illustration="house-confused"
              action={RestartButton}
            />
          )
        case ResidentAppErrorCode.CONFIRM_OPERATION_CANCELLED:
          return (
            <Error
              title={title}
              message={t('confirmationError.cancelled')}
              illustration="house-confused"
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_IDENTITY_MISMATCH:
          return (
            <Error
              title={title}
              message={t('confirmationError.identityMismatch')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_NO_ATTRIBUTES:
          return (
            <Error
              title={title}
              message={t('confirmationError.noAttributes')}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_NO_IDENTITY:
          return (
            <Error
              title={title}
              message={t('confirmationError.noIdentity')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_NO_IDENTITY_VERIFIER:
          return (
            <Error
              title={title}
              message={t('confirmationError.noIdentityVerifier')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_NO_USER:
          return (
            <Error
              title={title}
              message={t('confirmationError.noUser')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.TERMINATE_CONTRACT_INIT_CONFIRM_ERROR:
          return (
            <Error
              title={title}
              message={t('confirmationError.initFailed')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.ADD_TENANT_INIT_CONFIRM_ERROR:
          return (
            <Error
              title={title}
              message={t('confirmationError.addTenantInitFailed')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.TERMINATE_CONTRACT_MISSING_RESIDENT_CREDENTIALS:
          return (
            <Error
              title={title}
              message={t(
                'terminateContract:confirmationError.missingResidentCredentials'
              )}
              illustration="house-confused"
            />
          )

        case ResidentAppErrorCode.TERMINATE_CONTRACT_PENDING_REQUEST:
          return (
            <Error
              title={title}
              message={t(
                'terminateContract:confirmationError.pendingTerminationRequest'
              )}
              illustration="house-confused"
            />
          )

        case ResidentAppErrorCode.TERMINATE_CONTRACT_REQUEST_UNAVAILABLE:
          return (
            <Error
              title={title}
              message={t('errors.terminationRequestUnavailable')}
              illustration="house-confused"
            />
          )

        default:
      }
    }

    return <Error message={t('errors.outOfService')} />
  }

  return (
    <ErrorBoundary fallbackRender={fallbackRender}>{children}</ErrorBoundary>
  )
}
